/**
 * Running an addon, and managing the ones you have.
 *
 * The host half of the sandbox described in lib/addons.ts. Two things here
 * are load-bearing and should not be softened later:
 *
 *   1. The frame is `sandbox="allow-scripts"` and nothing else. Adding
 *      allow-same-origin would give the addon this app's origin, and with it
 *      localStorage, the session token, and the whole DOM. The entire design
 *      rests on that attribute.
 *
 *   2. Every message is matched against the specific frame it arrived from
 *      before it is answered. An addon says nothing about who it is; its
 *      identity is the window the message came on. That is what stops one
 *      addon reading another's saved data by claiming to be it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/stores/session';
import { api } from '@/lib/api';
import { useRoute } from '@/lib/hash';
import {
  addAddon, addonData, listAddons, onAddonsChanged, removeAddon,
  sandboxHtml, setAddonData, setEnabled, validateAddon, SAMPLE_ADDON,
  type Addon
} from '@/lib/addons';
import { cn } from '@/lib/cn';
import { bridge } from '@/lib/bridge';
import { Switch } from './Switch';
import { Icon } from '@/icons/Icon';
import { Button, Card, CardTitle, Field, Input, Modal, Textarea, useConfirm, useToast } from './ui';

/** Re-renders whenever the installed list changes. */
/**
 * Whether a theme's panel colour is a light one.
 *
 * Parsed rather than kept as a list of theme ids, because the scenes and any
 * future theme would each have to be remembered on that list and none of
 * them would be. The colour itself already knows.
 */
function isLight(colour: string): boolean {
  const hex = colour.trim();
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  /* Plain perceived brightness. This only has to pick a side, so the sRGB
     curve that applyCustomAccent needs would be false precision here. */
  const y = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return y > 140;
}

function useAddons(): Addon[] {
  const [list, setList] = useState<Addon[]>(() => listAddons());
  useEffect(() => onAddonsChanged(() => setList(listAddons())), []);
  return list;
}

/* --------------------------------------------------------- one frame */

/** Whether a panel is folded away, remembered per addon. */
function foldKey(id: string): string { return 'pugo.addon.fold.' + id; }
function readFold(id: string): boolean {
  try { return localStorage.getItem(foldKey(id)) === '1'; } catch { return false; }
}

/** What a party addon is told about the party it is running in. */
export type PartyAddonContext = {
  partyId: string;
  key: string;
  gameId: string;
  hostId: string;
  meId: string;
  canEdit: boolean;
  members: { id: string; name: string; username: string }[];
  shared: Record<string, unknown>;
  players: Record<string, Record<string, unknown>>;
};

export function AddonFrame({ addon, maxHeight, party, badge, stretch, open, onOpen }: {
  addon: Addon;
  maxHeight?: number;
  /** Set when the frame runs inside a party: the addon can read and write the party's copy. */
  party?: PartyAddonContext;
  /** A word in the corner instead of ADDON. */
  badge?: string;
  /** Fill the height it is given; the addon scrolls inside. */
  stretch?: boolean;
  /**
   * Whether this one is open, when something outside is deciding.
   *
   * On Home the panels sit in a narrow rail one under another, and with
   * each remembering its own fold you could have six open at once - a
   * column of frames past the bottom of the screen, all of them running.
   * The slot holds the answer there so opening one closes the rest.
   * Left out, the frame keeps its own, which is what a party wants.
   */
  open?: boolean;
  onOpen?: (open: boolean) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const partyRef = useRef<PartyAddonContext | undefined>(party);
  partyRef.current = party;
  const [title, setTitle] = useState(addon.name);
  const [height, setHeight] = useState(120);
  const [foldedSaved, setFolded] = useState(() => readFold(addon.id));
  /* A stretched panel is the whole view - there is nothing to fold it into. */
  const folded = stretch ? false : (open === undefined ? foldedSaved : !open);
  const [full, setFull] = useState(false);
  /*
   * A cap, where the host gives one.
   *
   * On Home an addon sits in a narrow rail beside everything else. Left to ask
   * for its full 600px it made the whole page scroll past an empty main column,
   * and the wheel got caught inside the frame on the way. Capped, it scrolls
   * inside its own panel; "Show all" lets it have the room when you want it.
   */
  const capped = !!maxHeight && !full && height > maxHeight;
  const shown = capped ? maxHeight! : height;
  const fold = (on: boolean) => {
    /* Controlled from outside: it is the slot's answer to change, and it
       does not go to storage - which one is open is a this-session thing,
       not a preference worth keeping. */
    if (onOpen) { onOpen(!on); return; }
    setFolded(on);
    try { on ? localStorage.setItem(foldKey(addon.id), '1') : localStorage.removeItem(foldKey(addon.id)); } catch { /* storage off */ }
  };
  const me = useSession((s) => s.me);
  const route = useRoute();
  const toast = useToast();
  const lastNotify = useRef(0);
  const lastCopy = useRef(0);

  /*
   * The theme, passed in as plain custom properties so an addon can look
   * like the rest of the app without being able to read the app.
   *
   * Re-read when the theme changes. This was computed once with no
   * dependencies, so an addon kept whatever colours the app happened to be
   * wearing when the panel first mounted - switch to a light theme and the
   * panels stayed dark until a reload.
   */
  const theme = useSession((s) => s.settings?.theme);
  const accentSetting = useSession((s) => s.settings?.accent);
  const tokens = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const style = getComputedStyle(document.documentElement);
    const vars = ['--text', '--text-dim', '--text-mute', '--bg-0', '--bg-1', '--bg-2', '--bg-3', '--bg-4', '--accent', '--accent-hi', '--good', '--bad', '--warn', '--line']
      .map((name) => name + ':' + style.getPropertyValue(name).trim() + ';')
      .join('');
    /*
     * And which way round the theme is.
     *
     * Without this the panel came out white. An iframe whose document has a
     * transparent background falls through to the browser's own canvas, and
     * that canvas is white unless the document says it is dark - so every
     * addon sat in a white box in the middle of a dark app. color-scheme is
     * the thing that tells it, and it has to come from the app because the
     * sandbox cannot look out to ask.
     */
    const dark = style.getPropertyValue('--bg-1').trim();
    return 'color-scheme:' + (isLight(dark) ? 'light' : 'dark') + ';' + vars;
  }, [theme, accentSetting]);

  const html = useMemo(() => sandboxHtml(addon, tokens), [addon, tokens]);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const win = frame.current?.contentWindow;
      /*
       * Identity comes from the window, never from the message.
       *
       * An addon could claim any id it liked in the payload; it cannot fake
       * which frame the message physically arrived on.
       */
      if (!win || e.source !== win) return;

      const d = e.data as { pugo?: boolean; id?: number; kind?: string; payload?: unknown };
      if (!d || !d.pugo || !d.id) return;

      const reply = (result: unknown) => win.postMessage({ pugo: true, id: d.id, result }, '*');

      switch (d.kind) {
        case 'me':
          /* Exactly these fields. Not the address, not the token, not the
             settings - an addon has no business with any of them. */
          reply(me ? {
            username: me.username,
            displayName: me.displayName,
            level: me.level,
            xp: me.xp
          } : null);
          break;

        case 'route':
          reply(route.head);
          break;

        case 'storage.get':
          reply(addonData(addon.id));
          break;

        case 'storage.set': {
          const value = d.payload;
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const text = JSON.stringify(value);
            // A box, not a disk. Anything larger is a mistake or an attack.
            if (text.length <= 64_000) setAddonData(addon.id, value as Record<string, unknown>);
          }
          reply(true);
          break;
        }

        case 'title':
          setTitle(String(d.payload || addon.name).slice(0, 40));
          reply(true);
          break;

        case 'notify': {
          /* A toast, at most one every two seconds, trimmed short. */
          const now = Date.now();
          if (now - lastNotify.current > 2000) {
            lastNotify.current = now;
            toast(addon.name + ': ' + String(d.payload || '').slice(0, 120), 'good');
            reply(true);
          } else reply(false);
          break;
        }

        case 'party.get':
          reply(partyRef.current ?? null);
          break;

        case 'party.setShared':
        case 'party.setMine': {
          const ctx = partyRef.current;
          const value = d.payload;
          const isShared = d.kind === 'party.setShared';
          if (!ctx || (isShared && !ctx.canEdit) || (value !== null && (typeof value !== 'object' || Array.isArray(value)))) {
            reply(false);
            break;
          }
          api.patch('/api/parties/' + ctx.partyId + '/addons/' + ctx.key, isShared ? { shared: value } : { mine: value })
            .then(() => reply(true), () => reply(false));
          break;
        }

        case 'copy': {
          /* Text only, capped, and at most once a second. The host copies it,
             because a sandboxed frame is not allowed the clipboard itself. */
          const now = Date.now();
          const text = String(d.payload ?? '').slice(0, 5000);
          if (!text || now - lastCopy.current < 1000) { reply(false); break; }
          lastCopy.current = now;
          const desk = bridge();
          const done = desk?.copy ? desk.copy(text) : navigator.clipboard?.writeText(text);
          Promise.resolve(done).then(() => reply(true), () => reply(false));
          break;
        }

        case 'theme': {
          const style = getComputedStyle(document.documentElement);
          const read = (name: string) => style.getPropertyValue(name).trim();
          reply({ accent: read('--accent'), text: read('--text'), background: read('--bg-1'), surface: read('--bg-2'), good: read('--good'), bad: read('--bad') });
          break;
        }

        case 'height':
          // Clamped, so an addon cannot push the rest of the page off screen.
          setHeight(Math.max(60, Math.min(600, Number(d.payload) || 120)));
          reply(true);
          break;

        default:
          reply(null);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [addon, me, route.head, toast]);

  /* Tell the addon when you move to another page. */
  const sendRoute = () => frame.current?.contentWindow?.postMessage({ pugo: true, kind: 'event', name: 'route', payload: route.head }, '*');
  useEffect(() => { sendRoute(); }, [route.head]);

  /* Tell a party addon whenever the party's copy changes - including its own
     writes coming back, which the addon recognises and ignores. */
  const partyJson = party ? JSON.stringify(party) : '';
  useEffect(() => {
    if (!partyJson) return;
    frame.current?.contentWindow?.postMessage({ pugo: true, kind: 'event', name: 'party', payload: JSON.parse(partyJson) }, '*');
  }, [partyJson]);

  return (
    <Card className={cn('overflow-hidden p-0', stretch && 'flex min-h-0 flex-1 flex-col')}>
      <div className={cn('flex min-w-0 items-center gap-2 px-3 py-2', !folded && 'border-b border-line-soft')}>
        <Icon name="plug" size={13} className="flex-none text-accent-hi" />
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-bold">{title}</span>
        {badge ? (
          <span className="flex flex-none items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--good)_14%,transparent)] px-2 py-px text-[10.5px] font-bold tracking-[.04em] text-good uppercase">
            <i aria-hidden className="h-1.5 w-1.5 rounded-full bg-good motion-safe:animate-pulse" />
            {badge}
          </span>
        ) : null}
        {maxHeight && !stretch && !folded && (full || height > maxHeight) ? (
          <button
            type="button"
            onClick={() => setFull(!full)}
            className="flex-none rounded-[6px] px-1.5 py-0.5 text-[11.5px] font-semibold text-accent-hi outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            {full ? 'Show less' : 'Show all'}
          </button>
        ) : null}
        <button
          type="button"
          hidden={stretch}
          aria-expanded={!folded}
          aria-label={folded ? 'Open ' + addon.name : 'Fold ' + addon.name}
          onClick={() => fold(!folded)}
          className="grid h-6 w-6 flex-none place-items-center rounded-[6px] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <Icon name="chevronD" size={14} className={cn('transition-transform duration-200', folded && '-rotate-90')} />
        </button>
      </div>
      {/*
        A folded addon is not rendered at all.

        It used to keep its frame alive at zero height, which meant every addon
        anybody had ever switched on was a live document sitting in memory - a
        few of them and the app was carrying tens of megabytes for panels
        nobody could see. They are cheap to bring back: the frame is inline
        HTML, and an addon's state lives in storage, not in the frame.
      */}
      {folded ? null : (
        <iframe
          ref={frame}
          title={addon.name}
          // The one attribute the whole design rests on. Do not add
          // allow-same-origin: it would hand this app's origin to the addon.
          sandbox="allow-scripts"
          srcDoc={html}
          onLoad={sendRoute}
          style={stretch ? undefined : { height: shown }}
          className={cn('block w-full border-0 bg-transparent', stretch ? 'min-h-0 flex-1' : 'transition-[height] duration-200')}
        />
      )}
    </Card>
  );
}

/**
 * Every enabled addon, and one of them open.
 *
 * They each remembered their own fold, so on Home you could end up with six
 * frames open one under another - a column running past the bottom of the
 * screen with every one of them live, which is both unreadable and six
 * sandboxes doing work for panels you are not looking at.
 *
 * So the slot decides. Opening one closes the rest; pressing the open one
 * closes it and leaves nothing open, because "all shut" is a state somebody
 * wants and a one-of-N picker that cannot reach it is annoying.
 */
export function AddonSlot({ maxHeight }: { maxHeight?: number } = {}) {
  const list = useAddons().filter((a) => a.enabled);
  const [openId, setOpenId] = useState<string | null>(null);
  if (!list.length) return null;
  return (
    <div className="flex min-w-0 flex-col gap-[var(--gap)]">
      {list.map((a) => (
        <AddonFrame
          key={a.id}
          addon={a}
          maxHeight={maxHeight}
          open={openId === a.id}
          onOpen={(on) => setOpenId(on ? a.id : null)}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------- management */

export function AddonsCard() {
  const list = useAddons();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [adding, setAdding] = useState(false);

  return (
    <Card>
      <CardTitle
        icon="plug"
        title="Addons"
        note={list.length ? String(list.length) : undefined}
        actions={
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[12.5px] font-semibold text-accent-hi hover:underline"
          >
            Add one
          </button>
        }
      />

      <p className="mb-3 text-[12.5px] text-mute">
        Small panels that run on your machine only. They are kept in a sandbox with no
        network and no access to your account, so an addon can draw and remember its own
        notes and nothing else. They are never uploaded and nobody else sees them.
      </p>

      {list.length ? (
        <div className="flex flex-col gap-1.5">
          {list.map((a) => (
            <div key={a.id} className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-1 px-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold">{a.name}</span>
                <span className="block truncate text-[11.5px] text-mute">
                  {a.description || 'by ' + a.author}
                </span>
              </span>
              <Switch size="sm" checked={a.enabled} onChange={(v) => setEnabled(a.id, v)} label={'Enable ' + a.name} />
              <button
                type="button"
                hidden
                onClick={() => setEnabled(a.id, !a.enabled)}
                className={cn(
                  'flex-none rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors',
                  a.enabled ? 'bg-accent-soft text-accent-hi' : 'bg-surface-3 text-mute'
                )}
              >
                {a.enabled ? 'ON' : 'OFF'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (await confirm('Remove ' + a.name + '?', 'Its saved notes go with it.',
                    { confirmLabel: 'Remove', danger: true })) {
                    removeAddon(a.id);
                    toast('Removed', 'good');
                  }
                }}
                className="flex-none text-[12px] font-semibold text-mute transition-colors hover:text-bad"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="py-4 text-center text-[13px] text-mute">Nothing installed.</p>
      )}

      {adding ? <AddDialog onClose={() => setAdding(false)} /> : null}
      {dialog}
    </Card>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const toast = useToast();

  return (
    <Modal
      title="Add an addon"
      subtitle="It runs on this machine, in a sandbox, and is never uploaded."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={() => { setName('Hello'); setCode(SAMPLE_ADDON); }}>
            Use the example
          </Button>
          {/* An addon saved as a file. Its // @name, @author and @description lines fill the form. */}
          <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] px-3 text-[13.5px] font-semibold text-accent-hi hover:bg-surface-2">
            <Icon name="upload" size={15} />
            Load from file
            <input
              type="file"
              accept=".js,.txt"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                const text = await file.text();
                const meta = (key: string) => text.match(new RegExp('//\\s*@' + key + '\\s+(.+)'))?.[1]?.trim();
                setName((meta('name') ?? file.name.replace(/\.[^.]+$/, '')).slice(0, 40));
                setAuthor(meta('author') ?? '');
                setDescription(meta('description') ?? '');
                setCode(text);
              }}
            />
          </label>
          <div className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button
            kind="primary"
            onClick={() => {
              const bad = validateAddon(name, code);
              if (bad) { toast(bad, 'bad'); return; }
              addAddon({ name, author, description, code });
              toast('Added', 'good');
              onClose();
            }}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="mb-2 rounded-[var(--radius)] border border-line-soft bg-surface-2 p-3 text-[12px] text-mute">
        Only add code you understand or trust. It cannot reach your account or the internet,
        but it can still draw whatever it likes inside its own panel.
      </div>

      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My widget" maxLength={40} />
      </Field>

      <div className="grid gap-[var(--gap)] sm:grid-cols-2">
        <Field label="Author">
          <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="You" maxLength={40} />
        </Field>
        <Field label="What it does">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
        </Field>
      </div>

      <Field label="Code" hint="Uses the pugo.* API. See pugo.ink/developers for what is available.">
        <Textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          rows={10}
          spellCheck={false}
          placeholder="pugo.setTitle('Hi');"
          className="font-mono text-[12px]"
        />
      </Field>
    </Modal>
  );
}
